import React, { useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import {
  useCheckPanelUserSurveyAccessQuery,
  useGetFormDetailsQuery,
  useGetFormQuestionsQuery,
} from "../../store/api/apiSlice";
import {
  getPanelSurveyAccessBlock,
  getSurveyTrackingToken,
  persistSurveyTrackingToken,
  withSurveyQueryString,
} from "../../utils/surveyTracking";

const SurveyFlow = () => {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const withQuery = (path) => withSurveyQueryString(path, searchParams);
  const trackingToken = getSurveyTrackingToken(formId, searchParams);

  const navigate = useNavigate();

  const { data: formData, isLoading: formLoading } =
    useGetFormDetailsQuery(formId);
  const { data: questionsData, isLoading: questionsLoading } =
    useGetFormQuestionsQuery(formId, {
      skip: !formId,
    });
  const { data: surveyAccessData, isLoading: accessLoading, isError: accessError } =
    useCheckPanelUserSurveyAccessQuery(trackingToken ?? skipToken);

  useEffect(() => {
    persistSurveyTrackingToken(formId, searchParams);
  }, [formId, searchParams]);

  useEffect(() => {
    if (formLoading || questionsLoading) return;
    if (trackingToken && accessLoading) return;
    if (!formData || !questionsData) return;

    const accessBlock = accessError
      ? null
      : getPanelSurveyAccessBlock(surveyAccessData);

    if (accessBlock) {
      navigate(withQuery(`/form-submissions/panel-error-page/${formId}`), {
        replace: true,
        state: {
          reason: accessBlock,
          canRetryPayout: surveyAccessData?.can_retry_payout === true,
          payout: surveyAccessData?.payout || {
            canRetry: surveyAccessData?.can_retry_payout === true,
            payoutSent: false,
            payoutError: surveyAccessData?.payout_error || null,
            snackbar:
              surveyAccessData?.snackbar || surveyAccessData?.payout?.snackbar,
          },
        },
      });
      return;
    }

    const questionsList = Array.isArray(questionsData?.data)
      ? questionsData.data
      : Array.isArray(questionsData)
        ? questionsData
        : Array.isArray(formData?.data?.questions)
          ? formData.data.questions
          : [];

    const introQuestion = questionsList.find((q) => q.is_intro);

    if (introQuestion) {
      navigate(withQuery(`/form-submissions/intro/${formId}`));
      return;
    }

    const firstQuestion = [...questionsList].sort(
      (a, b) => (a?.number ?? 0) - (b?.number ?? 0)
    )[0];

    if (firstQuestion) {
      const firstQuestionNumber = firstQuestion?.number ?? 0;
      navigate(
        withQuery(`/form-submissions/question/${formId}/${firstQuestionNumber}`)
      );
      return;
    }

    navigate(withQuery(`/form-submissions/panel-error-page/${formId}`), {
      replace: true,
      state: {
        reason: "unavailable",
        message: "This survey is unavailable.",
      },
    });
  }, [
    formData,
    questionsData,
    surveyAccessData,
    accessError,
    formLoading,
    questionsLoading,
    accessLoading,
    trackingToken,
    formId,
    navigate,
    searchParams,
  ]);

  if (formLoading || questionsLoading || (trackingToken && accessLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return null;
};
export default SurveyFlow;
